import { getClientContext, getTelemetryClientConfig } from '@ms/yammer-telemetry-store';

import { getAuthToken } from '../authentication';

/**
 * Fallback token is used to prevent telemetry calls from being routed to yammer canary environment.
 * It is randomly picked from a list of tokens as a load balancing strategy (one token per cell).
 * https://www.yammer.com/microsoft.com/threads/87078827802624
 */
const getFallbackToken = () => {
  const fallbacks = getTelemetryClientConfig()?.reportAuthorizationFallbacks ?? ['<not configured>'];
  const randomIndex = Math.floor(Math.random() * fallbacks.length);

  return `${fallbacks[randomIndex]}-TELEMETRY`;
};

const addHeader = (header: string, value?: string) => (value ? { [header]: value } : undefined);

const addMeetingScopeHeadersIfAvailable = () => {
  const { meetingTenantId, teamsMeetingId, participantRegistrationId } = getClientContext();
  const routingAppId = '1';

  if (!meetingTenantId) {
    return {};
  }

  return {
    'X-Yam-Scope-Type': 'TEAMS_MEETING',
    'X-Yam-TenantId': routingAppId + '.' + meetingTenantId,
    ...addHeader('X-Yam-Scope-Entity-Id', teamsMeetingId),
    ...addHeader('X-Yam-Participant-RegistrationId', participantRegistrationId),
  };
};

const constructEventHeaders = (withAuthentication: boolean, token: string | null) => ({
  'X-Request-Id': crypto.randomUUID(),
  authorization: `Bearer ${token}`,
  ...addMeetingScopeHeadersIfAvailable(),
  ...(!withAuthentication && { 'Authorization-Receiver': getTelemetryClientConfig()?.reportAuthorizationReceiver }),
});

export type GetEventHeadersWithAuthentication = () => Promise<Record<string, string>>;
export const getEventHeadersWithAuthentication: GetEventHeadersWithAuthentication = async () =>
  constructEventHeaders(true, await getAuthToken());

export type GetEventHeadersWithoutAuthentication = () => Promise<Record<string, string>>;
export const getEventHeadersWithoutAuthentication: GetEventHeadersWithoutAuthentication = () =>
  Promise.resolve(constructEventHeaders(false, getFallbackToken()));

type GetEventHeadersForPageHide = () => Record<string, string>;
export const getEventHeadersForPageHide: GetEventHeadersForPageHide = () =>
  constructEventHeaders(false, getFallbackToken());
